import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

interface Booking {
  id: string;
  botId: string;
  sender: string;
  channel: string;
  variables: Record<string, string>;
  product?: { id: string; name: string; price: string | null };
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

const STATUS_COLORS: Record<string, string> = {
  pending:   '#f59e0b',
  confirmed: '#10b981',
  cancelled: '#ef4444',
  completed: '#6b7280',
};

const STATUS_LABELS: Record<string, string> = {
  pending:   'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

function fmt(ts: number) {
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function varLabel(key: string) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface Props {
  botId: string;
}

export default function BookingsPanel({ botId }: Props) {
  const [bookings, setBookings]   = useState<Booking[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [expanded, setExpanded]   = useState<string | null>(null);
  const [updating, setUpdating]   = useState<string | null>(null);
  const [filterStatus, setFilter] = useState<string>('all');
  const [notes, setNotes]         = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await apiFetch(`/api/bookings/${botId}`);
      const data = await res.json();
      if (data.success) setBookings(data.bookings || []);
      else setError(data.error || 'Failed to load bookings');
    } catch {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => { load(); }, [load]);

  async function updateBooking(bookingId: string, patch: { status?: string; notes?: string }) {
    setUpdating(bookingId);
    try {
      await apiFetch(`/api/bookings/${botId}/${bookingId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, ...(patch as Partial<Booking>), updatedAt: Date.now() } : b
      ));
    } catch {
      alert('Failed to update booking');
    } finally {
      setUpdating(null);
    }
  }

  const displayed = filterStatus === 'all'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  const counts = bookings.reduce((acc, b) => {
    acc[b.status] = (acc[b.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (loading) return (
    <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>Loading bookings…</div>
  );

  if (error) return (
    <div style={{ padding: 24, color: '#ef4444' }}>{error}</div>
  );

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as const).map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                border: '1.5px solid',
                borderColor: filterStatus === s ? '#5B4FE8' : '#e5e7eb',
                background: filterStatus === s ? '#5B4FE8' : '#fff',
                color: filterStatus === s ? '#fff' : '#374151',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {s === 'all' ? `All (${bookings.length})` : `${STATUS_LABELS[s]} (${counts[s] || 0})`}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          style={{ background: 'none', border: 'none', color: '#5B4FE8', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
        >
          ↻ Refresh
        </button>
      </div>

      {displayed.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
          <div>No bookings yet.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Bookings are captured automatically when a bot flow asks for date, time, or service.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {displayed.map(b => {
          const isOpen = expanded === b.id;
          const busy   = updating === b.id;
          return (
            <div
              key={b.id}
              style={{
                border: '1.5px solid',
                borderColor: isOpen ? '#5B4FE8' : '#e5e7eb',
                borderRadius: 10,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: isOpen ? '0 2px 12px rgba(91,79,232,.08)' : 'none',
              }}
            >
              {/* Row */}
              <div
                onClick={() => setExpanded(isOpen ? null : b.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
              >
                <span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                  background: STATUS_COLORS[b.status] || '#ccc', flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {b.variables?.customer_name || b.variables?.quote_company || b.variables?.advertiser_name || b.variables?.name || b.sender}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {b.product?.name ? `📌 ${b.product.name}` : ''}
                    {b.product?.name && (b.variables?.customer_phone || b.variables?.quote_contact || b.variables?.quantity) ? ' · ' : ''}
                    {b.variables?.customer_phone || b.variables?.quote_contact || ''}
                    {b.variables?.start_date ? ` · From ${b.variables.start_date}` : ''}
                    {b.variables?.quantity ? ` · ${b.variables.quantity} mo` : ''}
                    {b.product?.price ? ` · $${b.product.price}/mo` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                    background: `${STATUS_COLORS[b.status]}22`,
                    color: STATUS_COLORS[b.status],
                  }}>
                    {STATUS_LABELS[b.status]}
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmt(b.createdAt)}</span>
                </div>
                <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 4 }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #f3f4f6', padding: '14px 16px', background: '#fafafa' }}>
                  {/* All variables */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px 16px', marginBottom: 14 }}>
                    {Object.entries(b.variables || {}).map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>{varLabel(k)}</div>
                        <div style={{ fontSize: 13, color: '#111' }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>
                    Channel: {b.channel} · Last updated: {fmt(b.updatedAt)}
                    {b.notes ? ` · Note: ${b.notes}` : ''}
                  </div>

                  {/* Notes */}
                  <textarea
                    placeholder="Add internal notes…"
                    value={notes[b.id] ?? b.notes ?? ''}
                    onChange={e => setNotes(prev => ({ ...prev, [b.id]: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', borderRadius: 6, border: '1px solid #e5e7eb', padding: '6px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
                  />

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {b.status !== 'confirmed' && (
                      <button disabled={busy} onClick={() => updateBooking(b.id, { status: 'confirmed', notes: notes[b.id] ?? b.notes })}
                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: busy ? .6 : 1 }}>
                        ✓ Confirm
                      </button>
                    )}
                    {b.status !== 'completed' && (
                      <button disabled={busy} onClick={() => updateBooking(b.id, { status: 'completed', notes: notes[b.id] ?? b.notes })}
                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#6b7280', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: busy ? .6 : 1 }}>
                        ✓ Mark Complete
                      </button>
                    )}
                    {b.status !== 'cancelled' && (
                      <button disabled={busy} onClick={() => updateBooking(b.id, { status: 'cancelled', notes: notes[b.id] ?? b.notes })}
                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: busy ? .6 : 1 }}>
                        ✕ Cancel
                      </button>
                    )}
                    {(notes[b.id] !== undefined && notes[b.id] !== (b.notes ?? '')) && (
                      <button disabled={busy} onClick={() => updateBooking(b.id, { notes: notes[b.id] })}
                        style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: busy ? .6 : 1 }}>
                        Save Note
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
